import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  LayoutDashboard,
  Plus,
  Trash2,
  Copy,
  Eye,
  Grid3X3,
  BarChart3,
  List,
  Table2,
  Star,
  StarOff,
  Settings,
} from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function DashboardCustomizationPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('layouts');
  const [showLayoutDialog, setShowLayoutDialog] = useState(false);
  const [showWidgetCatalog, setShowWidgetCatalog] = useState(false);
  const [selectedLayout, setSelectedLayout] = useState<any>(null);
  const [newLayoutName, setNewLayoutName] = useState('');

  // Fetch layouts
  const { data: layouts = [], isLoading: layoutsLoading } = useQuery({
    queryKey: ['dashboard-layouts'],
    queryFn: () => api.getDashboardLayouts(),
  });

  // Fetch default layout
  const { data: defaultLayout } = useQuery({
    queryKey: ['dashboard-default-layout'],
    queryFn: () => api.getDefaultDashboardLayout(),
  });

  // Fetch widgets
  const { data: widgets = [] } = useQuery({
    queryKey: ['dashboard-widgets'],
    queryFn: () => api.getAvailableWidgets(),
  });

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['widget-categories'],
    queryFn: () => api.getWidgetCategories(),
  });

  // Create layout mutation
  const createLayoutMutation = useMutation({
    mutationFn: (name: string) => api.createDashboardLayout({
      layout_name: name,
      widgets: [],
      is_default: layouts?.length === 0,
    }),
    onSuccess: () => {
      toast({ title: 'Layout created' });
      setShowLayoutDialog(false);
      setNewLayoutName('');
      queryClient.invalidateQueries({ queryKey: ['dashboard-layouts'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Delete layout mutation
  const deleteLayoutMutation = useMutation({
    mutationFn: (id: string) => api.deleteDashboardLayout(id),
    onSuccess: () => {
      toast({ title: 'Layout deleted' });
      queryClient.invalidateQueries({ queryKey: ['dashboard-layouts'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Duplicate layout mutation
  const duplicateLayoutMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.duplicateDashboardLayout(id, name),
    onSuccess: () => {
      toast({ title: 'Layout duplicated' });
      queryClient.invalidateQueries({ queryKey: ['dashboard-layouts'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Reset to default mutation
  const resetMutation = useMutation({
    mutationFn: () => api.resetDashboardToDefault(),
    onSuccess: () => {
      toast({ title: 'Layout reset to default' });
      queryClient.invalidateQueries({ queryKey: ['dashboard-layouts'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const getWidgetIcon = (type: string) => {
    switch (type) {
      case 'chart': return BarChart3;
      case 'stat': return List;
      case 'table': return Table2;
      case 'list': return List;
      default: return Grid3X3;
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      inventory: 'bg-blue-100 text-blue-800',
      procurement: 'bg-green-100 text-green-800',
      finance: 'bg-purple-100 text-purple-800',
      compliance: 'bg-orange-100 text-orange-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const groupedWidgets = widgets?.reduce((acc: any, widget: any) => {
    if (!acc[widget.category]) acc[widget.category] = [];
    acc[widget.category].push(widget);
    return acc;
  }, {});

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <LayoutDashboard className="h-8 w-8" />
            Dashboard Customization
          </h1>
          <p className="text-muted-foreground">
            Customize your dashboard layout and widgets
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowWidgetCatalog(true)}>
            <Settings className="mr-2 h-4 w-4" />
            Widget Catalog
          </Button>
          <Button onClick={() => setShowLayoutDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New Layout
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="layouts">My Layouts</TabsTrigger>
          <TabsTrigger value="widgets">Available Widgets</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="layouts">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>My Dashboard Layouts</CardTitle>
                <Button variant="outline" size="sm" onClick={() => resetMutation.mutate()}>
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Reset to Default
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {layouts?.map((layout: any) => (
                  <Card key={layout.layout_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2">
                          <LayoutDashboard className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <h3 className="font-semibold">{layout.layout_name}</h3>
                            {layout.is_default && (
                              <Badge variant="default" className="mt-1">
                                <Star className="mr-1 h-3 w-3" /> Default
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Widgets</span>
                          <span>{layout.layout_config?.widgets?.length || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Public</span>
                          <span>{layout.is_public ? 'Yes' : 'No'}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                        <Button variant="outline" size="sm" className="flex-1">
                          <Eye className="mr-1 h-4 w-4" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const name = prompt('Enter new layout name:');
                            if (name) duplicateLayoutMutation.mutate({ id: layout.layout_id, name });
                          }}
                        >
                          <Copy className="mr-1 h-4 w-4" />
                          Duplicate
                        </Button>
                        {!layout.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              if (confirm('Delete this layout?')) {
                                deleteLayoutMutation.mutate(layout.layout_id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {layouts?.length === 0 && (
                  <div className="col-span-full text-center py-12 text-muted-foreground">
                    <LayoutDashboard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No custom layouts yet</p>
                    <p className="text-sm">Create a new layout to get started</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="widgets">
          <Card>
            <CardHeader>
              <CardTitle>Available Widgets</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(groupedWidgets || {}).map(([category, categoryWidgets]: [string, any]) => (
                  <div key={category}>
                    <h3 className="font-semibold mb-3 capitalize">
                      <Badge className={getCategoryColor(category)}>{category}</Badge>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {categoryWidgets?.map((widget: any) => {
                        const Icon = getWidgetIcon(widget.widget_type);
                        return (
                          <div
                            key={widget.widget_id}
                            className="flex items-start gap-3 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                          >
                            <div className="p-2 bg-primary/10 rounded-lg">
                              <Icon className="h-5 w-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm">{widget.widget_name}</h4>
                              <p className="text-xs text-muted-foreground truncate">
                                {widget.description}
                              </p>
                              <Badge variant="outline" className="mt-2 text-xs">
                                {widget.widget_type}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <CardTitle>Dashboard Preview</CardTitle>
            </CardHeader>
            <CardContent>
              {defaultLayout?.layout_config?.widgets?.length > 0 ? (
                <div className="grid grid-cols-12 gap-4">
                  {defaultLayout.layout_config.widgets.map((widget: any, index: number) => {
                    const Icon = getWidgetIcon(
                      widgets?.find((w: any) => w.widget_key === widget.widget_key)?.widget_type || 'stat'
                    );
                    return (
                      <div
                        key={index}
                        className="col-span-12 lg:col-span-4 border rounded-lg p-4 bg-muted/30"
                        style={{
                          gridColumn: `span ${widget.w || 4}`,
                          gridRow: `span ${widget.h || 2}`,
                        }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm font-medium">
                            {widgets?.find((w: any) => w.widget_key === widget.widget_key)?.widget_name || widget.widget_key}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Widget content will be rendered here
                        </p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <LayoutDashboard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No widgets in this layout</p>
                  <p className="text-sm">Add widgets to see them here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Layout Dialog */}
      <Dialog open={showLayoutDialog} onOpenChange={setShowLayoutDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Layout</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Layout Name</label>
              <Input
                placeholder="My Custom Dashboard"
                value={newLayoutName}
                onChange={(e) => setNewLayoutName(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLayoutDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createLayoutMutation.mutate(newLayoutName)}
              disabled={!newLayoutName}
            >
              Create Layout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Widget Catalog Dialog */}
      <Dialog open={showWidgetCatalog} onOpenChange={setShowWidgetCatalog}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Widget Catalog</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            <div className="space-y-6 p-1">
              {Object.entries(groupedWidgets || {}).map(([category, categoryWidgets]: [string, any]) => (
                <div key={category}>
                  <h3 className="font-semibold mb-3 capitalize">
                    <Badge className={getCategoryColor(category)}>{category}</Badge>
                  </h3>
                  <div className="space-y-2">
                    {categoryWidgets?.map((widget: any) => {
                      const Icon = getWidgetIcon(widget.widget_type);
                      return (
                        <div
                          key={widget.widget_id}
                          className="flex items-start gap-3 p-3 border rounded-lg"
                        >
                          <div className="p-2 bg-primary/10 rounded-lg">
                            <Icon className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-sm">{widget.widget_name}</h4>
                            <p className="text-xs text-muted-foreground">
                              {widget.description}
                            </p>
                          </div>
                          <Badge variant="outline">{widget.widget_type}</Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
